// Read-only data fetches for the teacher dashboard (Phase E3b). All reads are
// blanket-gated by the Phase E1 permissions in firestore.rules (viewStudents /
// viewSchedule); these helpers do no writes. Queries are single-field so they
// need no composite indexes; ordering/scoping happens client-side.
import {
	collection,
	getDocs,
	query,
	where,
} from 'firebase/firestore';
import { db } from '../firebase';

const fullNameOf = (s) =>
	s.fullName || `${s.firstName || ''} ${s.lastName || ''}`.trim();

// Active students roster (gated viewStudents). Read-only — teachers never write
// students. Sorted by name client-side.
export async function fetchActiveStudents() {
	const snap = await getDocs(
		query(collection(db, 'students'), where('status', '==', 'Active'))
	);
	return snap.docs
		.map((d) => ({ id: d.id, ...d.data() }))
		.sort((a, b) => fullNameOf(a).localeCompare(fullNameOf(b)));
}

// The run teachers should see: the newest RELEASED one. generateTeacherSchedule
// mints runs as status:'draft' and the releaseScheduleRun callable flips them to
// 'released', so an admin can generate and edit without the half-built schedule
// flashing into the teacher view — the previously released run keeps serving
// until the new one is released. A run with NO status predates the field and
// counts as released. Exported for direct unit testing.
export function pickReleasedRun(runs) {
	return runs.find((r) => r.status !== 'draft') || null;
}

// "YYYY-MM-DD" of a run bound. Run bounds are stored as UTC midnight / UTC
// end-of-day of the studio date, so their UTC date IS the studio date (the
// same reading as functions/lessonTeacher.js → toDateStr).
export function runDateKey(v) {
	if (!v) return null;
	if (typeof v === 'string') return v.slice(0, 10);
	const d = typeof v.toDate === 'function' ? v.toDate() : v;
	return d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : null;
}

const runCreatedMs = (run) => {
	const c = run && run.createdAt;
	if (!c) return 0;
	if (typeof c.toMillis === 'function') return c.toMillis();
	const d = typeof c.toDate === 'function' ? c.toDate() : new Date(c);
	return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

// The run that owns one studio date: the newest RELEASED run (by createdAt)
// whose range covers it. The client twin of runForDate in
// functions/lessonTeacher.js, which the pay stamp and Change teachers already
// use, so a screen and a pay record agree about who teaches a day.
//
// WHY PER DATE (2026-10-22). Teachers used to see the ONE newest released run.
// Releasing a run that starts later (Oct 26 → 31, released on Oct 22) then
// hid the run still covering today, and every lesson on Today, My Schedule
// and the calendar lost its teacher with nothing deleted. Releasing next
// month before this one ends is the normal workflow, so the newest run can
// never be assumed to cover the day being shown.
export function runForDate(runs, dateKey) {
	let best = null;
	for (const r of runs || []) {
		if (!r || r.status === 'draft') continue;
		const from = runDateKey(r.rangeStart);
		const to = runDateKey(r.rangeEnd);
		if (!from || !to || dateKey < from || dateKey > to) continue;
		if (!best || runCreatedMs(r) > runCreatedMs(best)) best = r;
	}
	return best;
}

const addDayKey = (key, n) => {
	const d = new Date(`${key}T00:00:00Z`);
	d.setUTCDate(d.getUTCDate() + n);
	return d.toISOString().slice(0, 10);
};

// The released runs that own at least one date on or after `fromKey`, newest
// first. Runs entirely outside the window cost no assignment reads; a run
// wholly overshadowed by newer ones is dropped too.
export function runsOwningDatesFrom(runs, fromKey) {
	const owners = [];
	for (const r of runs || []) {
		if (!r || r.status === 'draft') continue;
		const from = runDateKey(r.rangeStart);
		const to = runDateKey(r.rangeEnd);
		if (!from || !to || to < fromKey) continue;
		for (let d = from < fromKey ? fromKey : from; d <= to; d = addDayKey(d, 1)) {
			if (runForDate(runs, d) === r) {
				owners.push(r);
				break;
			}
		}
	}
	return owners.sort((a, b) => runCreatedMs(b) - runCreatedMs(a));
}

// How far back the teacher-facing readers look. Lesson nudges look back over
// recent days and the calendar shows the month around today; eight weeks
// covers both, matching the generator's own backlog window.
export const SCHEDULE_LOOKBACK_DAYS = 56;

const localDayKey = (date) =>
	`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

// The rotation teachers see, per date: each date's assignments come from the
// run that owns that date (runForDate above), across every released run that
// owns a date in the last SCHEDULE_LOOKBACK_DAYS or later. Plus a
// teacherId→name map (gated viewSchedule). Assignments carry `dayLocationKey`
// ("YYYY-MM-DD::LOCATION") and `teacherId` (a teachers doc id). "Own vs full
// studio" is a client-side filter on teacherId — the rules don't scope
// schedule reads per-teacher.
//
// `run` is the newest contributing run, kept for callers that only need to
// know a schedule exists; `runs` is every contributing run (coverage requests
// are filed per run).
//
// Every run is read (no limit): there are ~50 and a limit is exactly the
// window that once let drafts push the released run out of sight (2026-08-30).
export async function fetchLatestSchedule({ now = new Date() } = {}) {
	const runSnap = await getDocs(collection(db, 'scheduleRuns'));
	const allRuns = runSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
	const fromKey = addDayKey(localDayKey(now), -SCHEDULE_LOOKBACK_DAYS);
	const runs = runsOwningDatesFrom(allRuns, fromKey);
	if (runs.length === 0) {
		return { run: null, runs: [], assignments: [], teachersById: {}, teachers: [] };
	}

	const [assignSnaps, teacherSnap] = await Promise.all([
		Promise.all(
			runs.map((r) =>
				getDocs(query(collection(db, 'scheduleAssignments'), where('runId', '==', r.id)))
			)
		),
		getDocs(collection(db, 'teachers')),
	]);

	const teachersById = {};
	// Active flag too, for pickers that must not offer a retired teacher
	// (Today's reassign control, 2026-10-04). teachersById keeps every name,
	// inactive included, so an old assignment still reads correctly.
	const teachers = [];
	teacherSnap.forEach((d) => {
		teachersById[d.id] = d.data().name || 'Teacher';
		teachers.push({ id: d.id, name: d.data().name || 'Teacher', active: d.data().active !== false });
	});

	const assignments = [];
	assignSnaps.forEach((snap, i) => {
		const run = runs[i];
		snap.docs.forEach((d) => {
			const data = d.data();
			const [date, location] = String(data.dayLocationKey || '::').split('::');
			// Only the dates this run owns: a date a newer released run also
			// covers is that run's, so two runs never both answer for one day.
			if (!date || runForDate(runs, date) !== run) return;
			assignments.push({
				id: d.id,
				runId: run.id,
				teacherId: data.teacherId || null,
				date,
				location: location || null,
				// The materializer's externalId for this occurrence
				// (`timeslot_<slotId>_<originDate>`), carried so a reader can
				// resolve the teacher PER LESSON rather than per day+location —
				// a timeslot rule can hand one slot to a different teacher, and
				// dayLocationKey cannot express that. Additive: existing callers
				// ignore it. See services/rotationLookup.js.
				sourceEventId: data.sourceEventId || null,
			});
		});
	});
	assignments.sort((a, b) => a.date.localeCompare(b.date));

	return { run: runs[0], runs, assignments, teachersById, teachers };
}

// Coverage requests for the given schedule run(s) (gated by the same
// viewSchedule read rule as the schedule). All statuses returned; callers
// filter — an approved or declined badge is information too.
export async function fetchCoverageRequests(runIds) {
	const ids = (Array.isArray(runIds) ? runIds : [runIds]).filter(Boolean);
	if (ids.length === 0) return [];
	const snaps = await Promise.all(
		ids.map((id) => getDocs(query(collection(db, 'coverageRequests'), where('runId', '==', id))))
	);
	return snaps
		.flatMap((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() })))
		.sort((a, b) => (a.date < b.date ? -1 : 1));
}
